<template>
  <NuxtLink
    class="relative rounded-md py-3 font-sans text-base font-bold uppercase outline-0 transition lg:py-4 lg:text-xl"
    :class="[
      buttonClass,
      !arrowRight
        ? 'inline-flex items-center px-8 text-center lg:px-14'
        : 'flex items-center justify-center px-2',
    ]"
    :to="removeCmsDomain(link)"
    :target="external ? '_blank' : '_self'"
    :rel="external ? 'noopener noreferrer' : undefined"
  >
    <Stars v-if="star" :class="starClass" />
    <span v-if="title" :class="[classNameText, 'w-full']">{{ title }}</span>
    <ArrowRight v-if="arrowRight" class="ml-4" />
  </NuxtLink>
</template>

<script setup lang="ts">
// Sibling: packages/astro-ui/src/components/ui/Link.vue
import { computed, inject, ref, type Ref } from 'vue'
import ArrowRight from './Icons/ArrowRight.vue'
import Stars from './Icons/Stars.vue'
import { removeCmsDomain } from '../../utils/urlFilter'

interface ILinkProps {
  title?: string
  classNameText?: string
  type?: 'fill' | 'outline'
  color?: 'blue' | 'pink' | 'gold'
  link: string
  external?: boolean
  arrowRight?: boolean
  star?: boolean
}

const props = withDefaults(defineProps<ILinkProps>(), {
  type: 'fill',
  color: 'blue',
  arrowRight: false,
  star: false,
})

const settings = inject<Ref<string[]>>('settings', ref([]))
const useDarkMode = computed(() => settings.value?.includes('dark_theme') ?? false)

const buttonClass = computed(() => ({
  'bg-blue text-white hover:bg-blue-dark border-2 border-blue hover:border-blue-dark shadow-[0_5px_15px_0px_rgba(0,155,181,0.22)] hover:shadow-none':
    props.type === 'fill' && props.color === 'blue',
  'bg-white text-blue border-2 border-blue hover:border-blue-dark hover:text-blue-dark shadow-[0_5px_15px_0px_rgba(0,155,181,0.22)] hover:shadow-none':
    props.type === 'outline' && props.color === 'blue' && !useDarkMode.value,
  'bg-none text-blue border-2 border-blue hover:border-blue-dark hover:text-blue-dark shadow-[0_5px_15px_0px_rgba(0,155,181,0.22)] hover:shadow-none':
    props.type === 'outline' && props.color === 'blue' && useDarkMode.value,
  'bg-pink text-white hover:bg-pink-dark border-2 border-pink hover:border-pink-dark shadow-[0_5px_15px_0px_rgba(171,8,98,0.22)] hover:shadow-none':
    props.type === 'fill' && props.color === 'pink',
  'bg-white text-pink border-2 border-pink hover:border-pink-dark hover:text-pink-dark shadow-[0_5px_15px_0px_rgba(171,8,98,0.22)] hover:shadow-none':
    props.type === 'outline' && props.color === 'pink',
  'bg-gold-wall text-white hover:bg-gold border-2 border-gold-wall hover:border-gold shadow-[0_5px_13px_0_rgba(225,158,0,0.25)] hover:shadow-[0_7px_20px_0_rgba(225,158,0,0.35)]':
    props.type === 'fill' && props.color === 'gold',
  'bg-white text-gold-wall border-2 border-gold-wall hover:border-gold hover:text-gold shadow-[0_5px_13px_0_rgba(225,158,0,0.25)] hover:shadow-[0_7px_20px_0_rgba(225,158,0,0.35)]':
    props.type === 'outline' && props.color === 'gold',
}))

const starClass = computed(() => {
  const baseClasses = 'mr-2 h-6 w-6'

  if (props.color === 'gold') {
    return `${baseClasses} text-[#FCDF3B]`
  }

  return baseClasses
})
</script>
